import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as RadixSelect from '@radix-ui/react-select';
import {
  Squares2X2Icon,
  RocketLaunchIcon,
  ListBulletIcon,
  ShieldCheckIcon,
  UsersIcon,
  ServerStackIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CheckIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  KeyIcon,
  LockClosedIcon,
  CheckCircleIcon,
  CheckBadgeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  BoltIcon,
  PowerIcon,
  DocumentTextIcon,
  SignalIcon,
  BellAlertIcon,
  NoSymbolIcon,
  ShareIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ClockIcon,
  EnvelopeIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { copyToClipboard } from '../lib/utils';
import { useTranslation } from 'react-i18next';

/* ============================================================
   Icon — semantic name -> uniform SVG (Heroicons)
   ============================================================ */
const ICONS = {
  dashboard: Squares2X2Icon,
  rocket: RocketLaunchIcon,
  logs: ListBulletIcon,
  shield: ShieldCheckIcon,
  users: UsersIcon,
  server: ServerStackIcon,
  chip: CpuChipIcon,
  warning: ExclamationTriangleIcon,
  settings: Cog6ToothIcon,
  sun: SunIcon,
  moon: MoonIcon,
  copy: ClipboardDocumentIcon,
  copied: ClipboardDocumentCheckIcon,
  clipboardList: ClipboardDocumentListIcon,
  check: CheckIcon,
  ellipsis: EllipsisVerticalIcon,
  close: XMarkIcon,
  plus: PlusIcon,
  refresh: ArrowPathIcon,
  search: MagnifyingGlassIcon,
  view: EyeIcon,
  key: KeyIcon,
  lock: LockClosedIcon,
  checkCircle: CheckCircleIcon,
  checkBadge: CheckBadgeIcon,
  trash: TrashIcon,
  download: ArrowDownTrayIcon,
  chevronDown: ChevronDownIcon,
  chevronRight: ChevronRightIcon,
  info: InformationCircleIcon,
  bolt: BoltIcon,
  power: PowerIcon,
  document: DocumentTextIcon,
  signal: SignalIcon,
  bell: BellAlertIcon,
  deny: NoSymbolIcon,
  share: ShareIcon,
  expand: ArrowsPointingOutIcon,
  collapse: ArrowsPointingInIcon,
  clock: ClockIcon,
  mail: EnvelopeIcon,
  star: StarIcon,
  starSolid: StarIconSolid,
} as const;

export type IconName = keyof typeof ICONS;

interface IconProps {
  name: IconName;
  className?: string;
  size?: number;
  /** Mirror the glyph in RTL (for directional icons such as "go to next" arrows). */
  mirrorInRtl?: boolean;
}

export const Icon: React.FC<IconProps> = ({ name, className, size, mirrorInRtl }) => {
  const Cmp = ICONS[name] || InformationCircleIcon;
  const classes = [className, mirrorInRtl ? 'rtl-flip' : ''].filter(Boolean).join(' ') || undefined;
  // Explicit `size` wins via inline style (unchanged behavior). When no size is
  // given, fall back to width/height *attributes* so heroicons never balloon to
  // their intrinsic size — context CSS (e.g. `.ent-btn svg { width: 15px }`)
  // still overrides these attributes where a specific size is intended.
  const style = size ? { width: size, height: size } : undefined;
  const fallback = size ? {} : { width: 18, height: 18 };
  return <Cmp className={classes} style={style} {...fallback} aria-hidden="true" />;
};

/* ============================================================
   Modal — centered dialog
   ============================================================ */
interface ModalProps {
  title?: React.ReactNode;
  icon?: IconName;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
}

export const Modal: React.FC<ModalProps> = ({ title, icon, onClose, children, footer, maxWidth }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portal to <body> so a modal opened from inside a transformed/clipped
  // ancestor (e.g. the sidebar) is still centered on the viewport.
  return ReactDOM.createPortal(
    <>
      <div className="ent-overlay" onClick={onClose} />
      <div className="ent-modal">
        <div className="ent-modal-card" style={maxWidth ? { maxWidth } : undefined} role="dialog" aria-modal="true">
          {(title || icon) && (
            <div className="ent-modal-head">
              {icon && <Icon name={icon} />}
              <h2>{title}</h2>
              <button className="icon-btn" style={{ marginInlineStart: 'auto' }} onClick={onClose} aria-label={t('components.ui.close')}>
                <Icon name="close" />
              </button>
            </div>
          )}
          <div className="ent-modal-body">{children}</div>
          {footer && <div className="ent-modal-foot">{footer}</div>}
        </div>
      </div>
    </>,
    document.body
  );
};

/* ============================================================
   Drawer / Sheet — right slide-out
   ============================================================ */
interface DrawerProps {
  title?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}

export const Drawer: React.FC<DrawerProps> = ({ title, onClose, children, footer, width }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="ent-overlay" onClick={onClose} />
      <aside className="ent-drawer" style={width ? { width } : undefined} role="dialog" aria-modal="true">
        <div className="ent-drawer-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('components.ui.close')}>
            <Icon name="close" />
          </button>
        </div>
        <div className="ent-drawer-body">{children}</div>
        {footer && <div className="ent-drawer-foot">{footer}</div>}
      </aside>
    </>
  );
};

/* ============================================================
   ContextMenu — ellipsis (...) action menu
   ============================================================ */
export interface MenuItem {
  label: string;
  icon?: IconName;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export const ContextMenu: React.FC<{ items: MenuItem[]; label?: string }> = ({ items, label = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        className="icon-btn"
        onClick={handleOpen}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="ellipsis" />
      </button>
      {open && menuPos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="ent-menu"
          style={{ position: 'absolute', top: menuPos.top, insetInlineEnd: menuPos.right }}
          role="menu"
        >
          {items.map((it, i) => (
            <button
              key={i}
              className={it.danger ? 'danger' : ''}
              disabled={it.disabled}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.icon && <Icon name={it.icon} />}
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

/* ============================================================
   CopyButton — hover copy-to-clipboard
   ============================================================ */
export const CopyButton: React.FC<{ value: string; title?: string }> = ({ value, title = 'Copy' }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={copy} title={copied ? t('components.ui.copied') : title} type="button">
      <Icon name={copied ? 'copied' : 'copy'} />
    </button>
  );
};

/** Monospace value with a hover copy affordance. */
export const CopyCell: React.FC<{ value: string; display?: string; className?: string }> = ({
  value,
  display,
  className,
}) => (
  <span className="copy-cell">
    <span className={className} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {display ?? value}
    </span>
    <CopyButton value={value} />
  </span>
);

/* ============================================================
   StatusPulse — non-clickable health indicator
   ============================================================ */
export const StatusPulse: React.FC<{ state: 'healthy' | 'unhealthy' | 'unknown'; label?: string }> = ({
  state,
  label,
}) => {
  const { t } = useTranslation();
  return (
    <span className={`status-pulse ${state}`} title={t('components.ui.health', { state })}>
      <span className="dot" />
      {label ?? state.charAt(0).toUpperCase() + state.slice(1)}
    </span>
  );
};

/* ============================================================
   CodeToken — bounded, styled syntax/path token
   ============================================================ */
export const CodeToken: React.FC<{ children: React.ReactNode; muted?: boolean }> = ({ children, muted }) => (
  <span className={`code-token ${muted ? 'muted' : ''}`}>{children}</span>
);

/* ============================================================
   ServerLogo — MCP server favicon with fallback to generic icon
   ============================================================ */
/**
 * Renders a server's logo, resolving its icon in priority order:
 *   1. Internal hosts  → the firewall's favicon proxy, which fetches the upstream
 *      server's own /favicon from inside the Docker network — something the browser
 *      can't reach directly.
 *   2. Anything else, or anything that fails to load → a generic server icon.
 *      Public domains are not looked up externally (air-gapped appliance).
 *
 * `proxyService` is the service/server name the firewall uses to resolve the
 * upstream (passed for internal servers); `apiUrl` is the firewall base URL.
 */
export const ServerLogo: React.FC<{
  hostname?: string;
  apiUrl: string;
  proxyService?: string;
  size?: number;
}> = ({ hostname = '', apiUrl, proxyService, size = 20 }) => {
  const [failed, setFailed] = useState(false);
  // Domain-style hostnames have a public favicon; bare/internal names use the proxy.
  const isPublicDomain = hostname.includes('.') && !hostname.endsWith('.local');

  // Public domains are not fetched from a third-party favicon service: the appliance
  // runs air-gapped, so they fall back to the generic icon like any other failure.
  let src: string | null = null;
  if (!isPublicDomain && proxyService) {
    src = `${apiUrl}/mcp-favicon/${encodeURIComponent(proxyService)}`;
  }

  if (failed || !src) {
    return <Icon name="server" size={size} />;
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{ borderRadius: 4, objectFit: 'contain', flexShrink: 0 }}
      onError={() => setFailed(true)}
    />
  );
};

/* ============================================================
   Select — Radix UI-powered styled dropdown
   Drop-in replacement for native <select> with full dark-theme
   support and keyboard accessibility.

   Usage:
     <Select value={val} onValueChange={setVal} placeholder="Pick one">
       <SelectItem value="a">Option A</SelectItem>
       <SelectItem value="b">Option B</SelectItem>
     </Select>

     // With groups:
     <Select ...>
       <SelectGroup label="Providers">
         <SelectItem value="openai">OpenAI</SelectItem>
       </SelectGroup>
     </Select>
   ============================================================ */

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  children: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  id,
  children,
}) => {
  const { t } = useTranslation();
  return (
  <RadixSelect.Root value={value || undefined} onValueChange={onValueChange} disabled={disabled}>
    <RadixSelect.Trigger
      id={id}
      className={`ent-select-trigger${className ? ` ${className}` : ''}`}
      aria-label={placeholder}
    >
      <RadixSelect.Value placeholder={<span className="ent-select-placeholder">{placeholder ?? t('components.ui.select')}</span>} />
      <RadixSelect.Icon asChild>
        <ChevronDownIcon className="ent-select-chevron" width={15} height={15} />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
    <RadixSelect.Portal>
      <RadixSelect.Content className="ent-select-content" position="popper" sideOffset={4} align="start">
        <RadixSelect.Viewport className="ent-select-viewport">{children}</RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  </RadixSelect.Root>
);
};

export const SelectItem: React.FC<{
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ value, children, disabled }) => (
  <RadixSelect.Item value={value} disabled={disabled} className="ent-select-item">
    <RadixSelect.ItemIndicator className="ent-select-item-indicator">
      <CheckIcon width={13} height={13} />
    </RadixSelect.ItemIndicator>
    <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
  </RadixSelect.Item>
);

export const SelectGroup: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <RadixSelect.Group>
    <RadixSelect.Label className="ent-select-group-label">{label}</RadixSelect.Label>
    {children}
  </RadixSelect.Group>
);

export const SelectSeparator: React.FC = () => (
  <RadixSelect.Separator className="ent-select-separator" />
);
